import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Leaf, Drop, Sun, Heart } from "@phosphor-icons/react";
import PageHero from "../components/PageHero";
import { IMAGES } from "../data";

const PRACTICES = [
  {
    Icon: Leaf,
    title: "Moringa as feed, not a marketing line",
    body:
      "We grow Moringa to a pharmaceutical-grade specification — no synthetic pesticides, no chemical fertilizers. The choice isn't only ecological, it is a quality-control decision: cleaner feed produces cleaner milk.",
  },
  {
    Icon: Sun,
    title: "Closed-loop agriculture",
    body:
      "Crops feed cattle, cattle waste enriches our soil, and the soil grows the next crop. We have eliminated dependency on external feed suppliers and synthetic inputs.",
  },
  {
    Icon: Drop,
    title: "Water stewardship",
    body:
      "Drip irrigation, rainwater harvesting, and recycled process water from the dairy facility. Every litre is metered.",
  },
  {
    Icon: Heart,
    title: "Animal welfare",
    body:
      "Open grazing, ample shaded shelter, on-payroll veterinary teams. Healthy cattle is not a virtue signal — it is a quality input.",
  },
];

const FUTURE_GOALS = [
  "Carbon-neutral processing facility by 2028",
  "100% renewable energy across owned acreage",
  "Biogas capture from herd waste — feed back into the grid",
  "Open-source feed protocols for partner farmers",
];

export default function Sustainability() {
  return (
    <div data-testid="sustainability-page">
      <PageHero
        overline="Sustainability"
        title={
          <>
            Pharmaceutical-grade.
            <br />
            <span className="italic text-terracotta">Chemical-free.</span>
          </>
        }
        subtitle="Sustainability isn't a separate program — it is the underlying logic of our supply chain. Every choice that lowers chemical dependency also lifts product quality."
        image={IMAGES.farm}
        testId="sustainability-hero"
      />

      {/* Moringa Spotlight */}
      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="moringa-spotlight">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 relative">
            <img src={IMAGES.moringa} alt="Moringa cultivation" className="w-full h-[500px] object-cover" />
            <div className="absolute bottom-6 left-6 badge-pill bg-bone/90">
              Pharmaceutical-Grade Cattle Feed
            </div>
          </div>
          <div className="lg:col-span-5">
            <span className="overline">The Moringa Decision</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6">
              Why we grow what most dairies{" "}
              <span className="italic text-terracotta">buy.</span>
            </h2>
            <p className="mt-8 text-olive-soft leading-relaxed">
              Moringa oleifera is one of the most nutrient-dense fodder crops
              on the planet — but commercial supply is inconsistent and rarely
              chemical-free. By growing it ourselves to a pharma-grade spec, we
              control the nutrition profile entering our herd, which directly
              shapes the chemistry of our milk.
            </p>
            <p className="mt-6 text-olive-soft leading-relaxed">
              That choice removes an entire class of feed-borne contamination
              risk and gives us a defensible quality story for buyers and
              regulators.
            </p>
          </div>
        </div>
      </section>

      {/* Practices Grid */}
      <section className="bg-sand py-24 lg:py-32" data-testid="sustainability-practices">
        <div className="max-w-8xl mx-auto px-6 lg:px-12">
          <span className="overline">Operating Principles</span>
          <h2 className="h-section text-4xl md:text-5xl mt-6 max-w-3xl">
            Sustainable by design, not by retrofit.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-14">
            {PRACTICES.map((p, i) => (
              <div
                key={p.title}
                className="card-flat p-8 lg:p-10 min-h-[240px] flex flex-col gap-6"
                data-testid={`practice-${i}`}
              >
                <p.Icon size={32} weight="duotone" className="text-terracotta" />
                <div>
                  <h3 className="font-heading text-2xl md:text-3xl text-olive-deep">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-olive-soft leading-relaxed">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Closed Loop */}
      <section className="max-w-8xl mx-auto px-6 lg:px-12 py-24 lg:py-36" data-testid="sustainability-closed-loop">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline">The Closed Loop</span>
            <h2 className="h-section text-4xl md:text-5xl mt-6">
              Crop → Cattle → Soil →{" "}
              <span className="italic text-terracotta">Crop.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 text-olive-soft leading-relaxed text-lg space-y-6">
            <p>
              Most dairy supply chains depend on at least three external
              ecosystems: a feed industry, a farmer network, and a logistics
              middle mile. Each adds variance. Each adds risk.
            </p>
            <p>
              By internalising every stage, we remove three categories of
              quality drift — and we earn the right to put a single,
              consistent batch profile in front of every buyer, every time.
            </p>
          </div>
        </div>
      </section>

      {/* Future Goals */}
      <section className="bg-olive-deep text-bone py-24 lg:py-32" data-testid="sustainability-goals">
        <div className="max-w-8xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="overline text-terracotta">Future Goals</span>
            <h2 className="font-heading text-4xl md:text-5xl mt-6 text-bone leading-tight">
              The 2028 commitments.
            </h2>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-bone/15">
              {FUTURE_GOALS.map((g, i) => (
                <li
                  key={i}
                  className="py-6 flex gap-6 items-start"
                  data-testid={`goal-${i}`}
                >
                  <span className="font-mono text-xs tracking-widest2 text-terracotta pt-1">
                    0{i + 1}
                  </span>
                  <span className="text-lg text-bone/85">{g}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-bone/40 text-bone px-7 py-4 mt-12 text-xs font-medium uppercase tracking-widest2 hover:bg-bone hover:text-olive-deep transition-colors"
              data-testid="sustainability-cta"
            >
              Discuss Joint Sustainability Programs
              <ArrowUpRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
